import { api } from "./base-config";
import {
  mapStringToTemporality,
  TemporalityEnum,
} from "common/enums/TemporalityEnum";
import { PosologyTypeEnum } from "common/enums/PosologyTypeEnum";
import {
  MedicationEntry,
  MedicationEntryPrescriptionStatusEnum,
  MedicationEntryType,
  sortMedicationEntries,
} from "model/medication-entry.model";
import { Posology, PosologyType } from "model/posology.model";
import { MedicationSearchResult } from "model/medication-search-result.model";
import { mapStringToMedicationTypeEnum } from "common/enums/MedicationTypeEnum";
import { PeriodEnum } from "common/enums/PeriodEnum";
import { mapStringToStatusEnum } from "common/enums/StatusTypeEnum";
import { DateMaskEnum } from "common/enums/DateMaskEnum";
import {
  MedicationStop,
  MedicationStopReasonEnum,
  MedicationStopTypeEnum,
} from "model/medication-stop.model";
import moment from "moment";
import { PaginatedMedicationRequest } from "model/paginated-medication-request.model";

/** ---- API CALLS ---- */
export const stopMedicationEntry = (
  patientId: number,
  caregiverId: number,
  entry: MedicationEntryType
): Promise<MedicationEntryType> => {
  return api
    .put(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/medicationEntries/${
        entry.dtoOrigin && entry.dtoOrigin.id
      }`,
      Object.assign({} as MedicationEntryDTO, entry.dtoOrigin, {
        subContactsIDs: entry.subContactsIDs,
        status: entry.status,
        medicationStops: mapToMedicationStopDTO([
          new MedicationStop({
            reasonType: MedicationStopReasonEnum.OTHER,
            startDate: moment().toDate(),
            type: MedicationStopTypeEnum.DEFINITIVE,
          }),
        ]),
      })
    )
    .then((response) => mapToMedicationEntryModel(response.data));
};

export const createMedicationEntries = (
  patientId: number,
  caregiverId: number,
  medicationEntries: Array<MedicationEntryType>
): Promise<Array<MedicationEntryType>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/medicationEntries/all`,
      mapToMedicationEntriesDTO(medicationEntries)
    )
    .then((response) => mapToMedicationEntries(response.data));
};

export const prefillForRepeat = (
  caregiverId: number,
  patientId: number,
  entry: MedicationEntryType
): Promise<MedicationEntry> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/medicationEntries/prefill`,
      [entry.dto]
    )
    .then((response) => {
      return new MedicationEntry(
        Object.assign({}, mapToMedicationEntryModel(response.data[0]), {
          dtoOrigin: entry.dto,
        })
      );
    });
};

export const prefillSearchResult = (
  caregiverId: number,
  patientId: number,
  medicationSearchResult: MedicationSearchResult
): Promise<MedicationEntry> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/medicationEntries/prefillSearchResult`,
      medicationSearchResult.dto
    )
    .then((response) => {
      return mapToMedicationEntries([response.data])[0];
    });
};

export const findActive = (
  caregiverId: number,
  patientId: number,
  paginatedRequest: PaginatedMedicationRequest
): Promise<Array<MedicationEntry>> => {
  return api
    .post(
      `/resources/secured/rs/caregivers/${caregiverId}/patients/${patientId}/medicationEntries/find`,
      paginatedRequest,
      {
        params: {
          status: "ACTIVE",
        },
      }
    )
    .then((response) =>
      sortMedicationEntries(mapToMedicationEntries(response.data))
    );
};

export const searchMedication = async (
  searchText: string
): Promise<Array<MedicationSearchResult>> => {
  return api
    .get(`/resources/secured/rs/medicationLookup/find`, {
      params: { title: searchText },
    })
    .then((response) => {
      return response.data.map((dto: MedicationSearchResultDTO) =>
        mapToMedicationSearchResult(dto)
      );
    });
};

/** ---- API MAPPING ---- */
/** ---- UI -> DTO ---- */
export const mapToMedicationStopDTO = (
  stops: Array<MedicationStop>
): Array<MedicationStopDTO> => {
  return stops.map((stop) => {
    return Object.assign({} as MedicationStopDTO, {
      "@type": "MedicationStop",
      type: stop.type,
      startDate: stop.startDate,
      startDateMask: DateMaskEnum.DDMMYYYYHHMM,
      reasonType: stop.reasonType,
    });
  });
};

export const mapToMedicationEntriesDTO = (
  entries: Array<MedicationEntryType>
) => {
  return entries.map((entry) => mapToMedicationDTO(entry));
};

const mapToMedicationDTO = (entry: MedicationEntryType): MedicationEntryDTO => {
  return Object.assign({} as MedicationEntryDTO, entry.dto, {
    subContactsIDs: entry.subContactsIDs,
    freeTextContent: entry.freeTextContent,
    temporality: entry.temporality,
    posology: mapToPrescriptionDTO(entry),
    endDate:
      entry.temporality === TemporalityEnum.CHRONIC ? null : entry.dto?.endDate,
    endDateMask:
      entry.temporality === TemporalityEnum.CHRONIC
        ? null
        : entry.dto?.endDateMask,
  });
};

const mapToPrescriptionDTO = (
  entry: MedicationEntryType
): PosologyDTO | undefined => {
  switch (entry.prescriptionStatus) {
    case MedicationEntryPrescriptionStatusEnum.EDITED:
    case MedicationEntryPrescriptionStatusEnum.NEW:
      return createNewPosologyDTO(entry.posology);
    case MedicationEntryPrescriptionStatusEnum.DEFAULT:
    case MedicationEntryPrescriptionStatusEnum.REPEAT:
      return entry.dto && entry.dto.posology;
  }
};

const createNewPosologyDTO = (
  posology: PosologyType
): PosologyDTO | undefined => {
  switch (posology.type) {
    case PosologyTypeEnum.FREE_TEXT:
      return {
        "@type": "FreeTextPosology",
        content: posology.posologyText,
        posologyText: posology.posologyText,
        periodUnit: posology.periodUnit,
        periodNumber: posology.periodNumber,
      };
    case PosologyTypeEnum.SIMPLE:
    case PosologyTypeEnum.REPEAT:
    case PosologyTypeEnum.PHASING:
    case PosologyTypeEnum.WEEKDAY:
    case PosologyTypeEnum.DATE:
      //No support for creating new Posology of this type
      return undefined;
  }
};

/** ---- DTO -> UI ---- */
const mapToMedicationEntries = (response: any): Array<MedicationEntry> => {
  const medicationEntriesSet = new Set<MedicationEntry>();
  if (response.length > 0) {
    response.forEach((entry: MedicationEntryDTO) => {
      medicationEntriesSet.add(mapToMedicationEntryModel(entry));
    });
  }
  return Array.from<MedicationEntry>(medicationEntriesSet);
};

const mapToMedicationEntryModel = (
  medicationEntryDTO: MedicationEntryDTO
): MedicationEntry => {
  return new MedicationEntry({
    id: medicationEntryDTO.id,
    status: mapStringToStatusEnum(medicationEntryDTO.status),
    subContactsIDs: medicationEntryDTO.subContactsIDs,
    prescriptionStatus: MedicationEntryPrescriptionStatusEnum.DEFAULT,
    freeTextContent: medicationEntryDTO.freeTextContent,
    temporality: mapStringToTemporality(medicationEntryDTO.temporality),
    posology: mapToPosologyModel(medicationEntryDTO.posology),
    medicationStops: mapToMedicationStop(medicationEntryDTO.medicationStops),
    dto: medicationEntryDTO,
  });
};

const mapToMedicationStop = (
  stops: Array<MedicationStopDTO>
): Array<MedicationStop> => {
  return (
    stops &&
    stops.map((stop) => {
      return Object.assign({} as MedicationStop, {
        type: stop.type,
        reasonType: stop.reasonType,
      });
    })
  );
};

const mapToPosologyModel = (posologyDTO: PosologyDTO): Posology => {
  let type: PosologyTypeEnum = PosologyTypeEnum.FREE_TEXT;
  let posologyText: string = "";
  let posology = new Posology();
  if (posologyDTO !== undefined) {
    if (posologyDTO["@type"] === "FreeTextPosology") {
      type = PosologyTypeEnum.FREE_TEXT;
      posologyText = posologyDTO.content || "";
    } else {
      type = mapToPosologyTypeEnum(posologyDTO.entries, posologyDTO.phasing);
      posologyText = posologyDTO.content || posologyDTO.posologyText || "";
    }
    posology = new Posology({
      type: type,
      posologyText: posologyText,
      periodNumber: posologyDTO.periodNumber,
      periodUnit: posologyDTO.periodUnit,
    });
  }
  return posology;
};

const mapToPosologyTypeEnum = (
  posologyEntryDTOs?: Array<PosologyEntryDTO>,
  phasing?: boolean
): PosologyTypeEnum => {
  if (posologyEntryDTOs !== undefined) {
    if (posologyEntryDTOs.length > 0) {
      switch (posologyEntryDTOs[0]["@type"]) {
        case "CodedPosologyPeriodEntry":
          return PosologyTypeEnum.SIMPLE;
        case "WeekdayCodedPosologyRegimeEntry":
          return PosologyTypeEnum.WEEKDAY;
        case "DateCodedPosologyRegimeEntry":
          return PosologyTypeEnum.DATE;
        case "DayNumberCodedPosologyRegimeEntry":
          return phasing ? PosologyTypeEnum.PHASING : PosologyTypeEnum.REPEAT;
        default:
          return PosologyTypeEnum.SIMPLE;
      }
    } else {
      return PosologyTypeEnum.SIMPLE;
    }
  }
  return PosologyTypeEnum.FREE_TEXT;
};

const mapToMedicationSearchResult = (
  dto: MedicationSearchResultDTO
): MedicationSearchResult => {
  const medicationSearchResult = new MedicationSearchResult();
  medicationSearchResult.name = dto.name;
  medicationSearchResult.cheapCategoryInfo = dto.info;
  medicationSearchResult.reimbursementCodes = dto.ssecr
    ? dto.ssecr.toLowerCase().split(" ")
    : [];
  medicationSearchResult.price = dto.price;
  medicationSearchResult.dto = dto;
  medicationSearchResult.type = mapStringToMedicationTypeEnum(dto.type);

  return medicationSearchResult;
};

/** ---- API DTO ---- */
export interface MedicationEntryDTO {
  readonly "@type": "MedicationEntry";
  readonly id: number;
  readonly status: string;
  readonly temporality: string;
  readonly freeTextContent: string;
  readonly subContactsIDs: Array<number>;
  readonly posology: PosologyDTO;
  readonly medication: MedicationDTO;
  readonly medicationStops: Array<MedicationStopDTO>;
  readonly endDate: Date;
  readonly endDateMask: DateMaskEnum;
}

interface PosologyDTO {
  readonly "@type": "CodedPosology" | "FreeTextPosology";
  readonly content?: string;
  readonly posologyText?: string;
  readonly entries?: Array<PosologyEntryDTO>;
  readonly periodNumber?: number;
  readonly periodUnit?: PeriodEnum;
  readonly phasing?: boolean;
}

interface PosologyEntryDTO {
  readonly "@type":
    | "CodedPosologyPeriodEntry"
    | "DateCodedPosologyRegimeEntry"
    | "CodedPosologyRegimeEntry"
    | "WeekdayCodedPosologyRegimeEntry"
    | "DayNumberCodedPosologyRegimeEntry";
}

interface MedicationStopDTO {
  readonly "@type": "MedicationStop";
  readonly type: "DEFINITIVE" | "TEMPORARY";
  readonly startDate: Date;
  readonly startDateMask: DateMaskEnum;
  readonly reasonType: "ALLERGY" | "INTOLERANCE" | "PATHOLOGY_CHANGE" | "OTHER";
}

interface MedicationDTO {
  readonly "@type": "Medication";
  readonly medicationCodeType: string;
  readonly medicationCodeVersion: string;
  readonly medicationCodeValue: string;
  readonly galenicForm: string;
  readonly narcotic: boolean;
  readonly name: string;
  readonly reimbursementCode: string;
  readonly atcCode: string;
  readonly content: string;
  readonly templateId: number;
  readonly productPackage: string;
  readonly vos: string;
  readonly paragraphsValue: string;
  readonly contentQuantityMultiplier: string;
  readonly administrationUnit: string;
  readonly otc: boolean;
  readonly renewable: boolean;
  readonly birthControl: boolean;
  readonly type: string;
}

interface MedicationSearchResultDTO {
  readonly "@type": "MedicationSearchResult";
  readonly atc: string;
  readonly cheapest: boolean;
  readonly cnkcode: string;
  readonly codeType: string;
  readonly content: string;
  readonly form: string;
  readonly index: number;
  readonly info: string;
  readonly law: string;
  readonly name: string;
  readonly price: number;
  readonly rema: number;
  readonly ssecr: string;
  readonly substance: boolean;
  readonly vmpGroupId: string;
  readonly innCnk: string;
  readonly type: string;
}
